import type { ChangeMessage } from "@tcg/realtime";
import type { ToastRule } from "@tcg/realtime/query";
import type { TFunction } from "i18next";

import type { ToastKind } from "@/lib/toast";

import { isOwnChange } from "./ownChanges";

export interface LiveAlert {
  kind: ToastKind;
  text: string;
}

/** Topics the alerts listen to (only those the person may read are subscribed). */
export const ALERT_TOPICS = [
  "staff:orders",
  "staff:payments",
  "staff:pos",
  "staff:inventory",
  "staff:consignment",
] as const;

const hintOf = (message: ChangeMessage, name: string): string | null => {
  const value = message.hint?.[name];
  return typeof value === "string" ? value : null;
};

/**
 * What pops up (T11-52 step 3): new order, "I have paid" notice from a customer, money received
 * (入金確認 pressed by someone else / automatic), a counter sale, the same card sold twice, a new
 * consignment request. Changes this tab made itself do not pop up.
 */
export function alertRules(t: TFunction): Array<ToastRule<LiveAlert>> {
  return [
    {
      topic: "staff:orders",
      entity: "order",
      action: "created",
      when: (m) => !isOwnChange(`order:${m.id}`),
      toast: () => ({ kind: "info", text: t("live.new_order") }),
    },
    {
      topic: "staff:orders",
      entity: "order",
      action: "payment_notice_received",
      toast: () => ({ kind: "info", text: t("live.transfer_notice") }),
    },
    {
      topic: "staff:payments",
      entity: "payment",
      action: "captured",
      // counter payments are part of the sale (own alert below); own 入金確認 is not news
      when: (m) =>
        hintOf(m, "registerId") === null && !isOwnChange(`order:${hintOf(m, "orderId") ?? ""}`),
      toast: () => ({ kind: "success", text: t("live.payment_received") }),
    },
    {
      topic: "staff:pos",
      entity: "pos_sale",
      action: "completed",
      toast: () => ({ kind: "info", text: t("live.pos_sale") }),
    },
    {
      entity: "oversell_conflict",
      action: "detected",
      toast: () => ({ kind: "error", text: t("live.oversell") }),
    },
    {
      topic: "staff:consignment",
      entity: "consignment_request",
      action: "created",
      toast: () => ({ kind: "info", text: t("live.consignment_request") }),
    },
  ];
}
